import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';

// ── Constants ─────────────────────────────────────────────────────────────────

const PERSONAL_CATEGORIES = [
    'Groceries', 'Takeaways', 'Coffee & Snacks', 'Eating Out',
    'Fuel & Transport', 'Subscriptions', 'Gym & Health', 'Clothing',
    'Kids', 'Home', 'Entertainment', 'Gifts', 'Debt payment', 'Other',
];
const BUSINESS_EXTRA = ['Office', 'Travel', 'Software'];

const CATEGORY_EMOJI = {
    'Groceries':       '🛒',
    'Takeaways':       '🍔',
    'Coffee & Snacks': '☕',
    'Eating Out':      '🍽️',
    'Fuel & Transport':'⛽',
    'Subscriptions':   '📺',
    'Gym & Health':    '🏋️',
    'Clothing':        '👗',
    'Kids':            '👶',
    'Home':            '🏠',
    'Entertainment':   '🎬',
    'Gifts':           '🎁',
    'Office':          '🖨️',
    'Travel':          '✈️',
    'Software':        '💻',
    'Debt payment':    '📉',
    'Other':           '💳',
};

function catEmoji(cat) { return CATEGORY_EMOJI[cat] || '💳'; }

const LS_LAST_CAT    = 'aidey_spending_lastCat';
const LS_CUSTOM_CATS = 'aidey_spending_customCats';

// ── Styles ────────────────────────────────────────────────────────────────────

const card = { background: '#fff', borderRadius: 14, padding: '16px 18px', marginBottom: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #e5e7eb' };
const inputStyle = { padding: '7px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#fff' };
const btnPrimary = { padding: '8px 16px', borderRadius: 10, border: 'none', background: '#1a2e3b', color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer' };
const btnDanger  = { padding: '4px 10px', borderRadius: 7, border: '1px solid #fca5a5', background: '#fff7f7', color: '#b91c1c', fontSize: 12, cursor: 'pointer' };

const fmtGBP = (n) => '£' + Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function fmtDate(isoDate) {
    const d = new Date(isoDate + 'T12:00:00');
    const now = new Date();
    const today = now.toISOString().slice(0, 10);
    const yesterday = new Date(now - 86400000).toISOString().slice(0, 10);
    if (isoDate === today)     return 'Today';
    if (isoDate === yesterday) return 'Yesterday';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function fmtShortDate(iso) {
    const d = new Date(iso + 'T12:00:00');
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function currentMonthStr() {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
}

// ── Weekly compact line ───────────────────────────────────────────────────────

function WeekLine({ weekSummary }) {
    if (!weekSummary) return null;
    return (
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: '8px 14px', marginBottom: 10, border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14 }}>📅</span>
            <span style={{ fontSize: 12, color: '#374151' }}>
                <strong>This week</strong> ({fmtShortDate(weekSummary.weekStart)}–{fmtShortDate(weekSummary.weekEnd)}):&nbsp;
                <strong style={{ color: '#1a2e3b' }}>{fmtGBP(weekSummary.total)}</strong> across {weekSummary.count} transaction{weekSummary.count !== 1 ? 's' : ''}
            </span>
        </div>
    );
}

// ── Insights panel ────────────────────────────────────────────────────────────

function InsightsPanel({ insights }) {
    if (!insights || insights.length === 0) return null;

    return (
        <div style={{ ...card, marginBottom: 12, background: '#fffbeb', border: '1px solid #fde68a' }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                💡 Insights
            </div>
            {insights.map((ins, i) => {
                const isChild = ins.isChildInsight;
                return (
                    <div key={i} style={{
                        padding: '10px 12px',
                        borderRadius: 10,
                        background: isChild ? '#f0fdf4' : '#fff',
                        border: isChild ? '1px solid #bbf7d0' : '1px solid #fef3c7',
                        marginBottom: i < insights.length - 1 ? 8 : 0,
                    }}>
                        {ins.memberName && (
                            <div style={{ fontSize: 10, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                                {ins.memberName}
                            </div>
                        )}
                        {ins.celebText && (
                            <div style={{ fontSize: 12, fontWeight: 700, color: '#059669', marginBottom: 4 }}>
                                {ins.celebText}
                            </div>
                        )}
                        <p style={{ fontSize: 13, color: '#374151', margin: 0, lineHeight: 1.5 }}>
                            {ins.text}
                        </p>
                        {ins.goalText && (
                            <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0', fontStyle: 'italic' }}>
                                {ins.goalText}
                            </p>
                        )}
                    </div>
                );
            })}
        </div>
    );
}

// ── Monthly report card ───────────────────────────────────────────────────────

function MonthlyReport({ report }) {
    if (!report || report.total === 0) return null;
    const top3 = (report.categories || []).slice(0, 3);
    const top3Sentence = top3.map(c =>
        `${fmtGBP(c.total)} on ${c.category.toLowerCase()}`
    ).join(', ');

    const deltaColour = report.delta > 0 ? '#ef4444' : '#10b981';
    const deltaLabel  = report.delta > 0
        ? `+${fmtGBP(report.delta)} vs last month`
        : report.delta < 0
            ? `${fmtGBP(report.delta)} vs last month`
            : 'same as last month';

    return (
        <div style={{ ...card, marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                This month's spending
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
                <span style={{ fontSize: 32, fontWeight: 800, color: '#1a2e3b', lineHeight: 1 }}>{fmtGBP(report.total)}</span>
                {report.prevTotal > 0 && (
                    <span style={{ fontSize: 12, fontWeight: 600, color: deltaColour }}>{deltaLabel}</span>
                )}
            </div>
            {top3.length > 0 && (
                <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 12px' }}>
                    {top3Sentence}.
                </p>
            )}

            {/* Per-category bars */}
            {(report.categories || []).map(c => {
                const pct = Math.round((c.total / report.total) * 100);
                const dColour = c.delta > 0 ? '#ef4444' : c.delta < 0 ? '#10b981' : '#9ca3af';
                return (
                    <div key={c.category} style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                            <span style={{ fontSize: 12, color: '#374151', fontWeight: 600 }}>
                                {catEmoji(c.category)} {c.category}
                            </span>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                {c.delta !== 0 && (
                                    <span style={{ fontSize: 11, color: dColour, fontWeight: 600 }}>
                                        {c.delta > 0 ? '+' : ''}{fmtGBP(c.delta)}
                                    </span>
                                )}
                                <span style={{ fontSize: 12, fontWeight: 700, color: '#1a2e3b' }}>{fmtGBP(c.total)}</span>
                            </div>
                        </div>
                        <div style={{ background: '#f3f4f6', borderRadius: 99, height: 7, overflow: 'hidden' }}>
                            <div style={{ width: `${pct}%`, background: '#f59e0b', height: '100%', borderRadius: 99, transition: 'width 0.4s' }} />
                        </div>
                    </div>
                );
            })}

            {/* Per-member breakdown */}
            {(report.memberTotals || []).length > 0 && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>By member</div>
                    {report.memberTotals.map(m => (
                        <div key={m.name} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#374151', marginBottom: 2 }}>
                            <span>{m.name}</span>
                            <span style={{ fontWeight: 700 }}>{fmtGBP(m.total)}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Quick-add form ────────────────────────────────────────────────────────────

const QAF_CSS = `
.sp-qaf {
    display: grid;
    grid-template-columns: 88px 1fr 150px 130px 100px 72px;
    gap: 8px;
    align-items: end;
    margin-bottom: 16px;
}
.sp-qaf-lbl {
    font-size: 11px; font-weight: 700; color: #6b7280; margin-bottom: 3px;
}
@media (max-width: 640px) {
    .sp-qaf {
        grid-template-columns: 88px 1fr;
    }
    .sp-qaf .sp-qaf-cat  { grid-column: 1; }
    .sp-qaf .sp-qaf-date { grid-column: 2; }
    .sp-qaf .sp-qaf-who  { grid-column: 1; }
    .sp-qaf .sp-qaf-add  { grid-column: 2; }
}
`;

function QuickAddForm({ categories, members, onAdd }) {
    const today = new Date().toISOString().slice(0, 10);
    const [form, setForm] = useState({
        amount: '', description: '', category: localStorage.getItem(LS_LAST_CAT) || 'Other',
        spentAt: today, householdMemberId: '', customCat: '',
    });
    const [useCustom, setUseCustom] = useState(false);
    const [saving, setSaving] = useState(false);

    function f(k) { return e => setForm(p => ({ ...p, [k]: e.target.value })); }

    async function save(e) {
        e.preventDefault();
        if (!form.amount || !form.description.trim()) return;
        const cat = useCustom ? form.customCat.trim() || 'Other' : form.category;
        setSaving(true);
        try {
            await onAdd({ ...form, category: cat, householdMemberId: form.householdMemberId || null });
            localStorage.setItem(LS_LAST_CAT, cat);
            if (useCustom && cat !== 'Other') {
                const existing = JSON.parse(localStorage.getItem(LS_CUSTOM_CATS) || '[]');
                if (!existing.includes(cat)) localStorage.setItem(LS_CUSTOM_CATS, JSON.stringify([...existing, cat]));
            }
            setForm(p => ({ ...p, amount: '', description: '', spentAt: today, customCat: '' }));
        } finally {
            setSaving(false);
        }
    }

    const gridCols = members.length > 0
        ? '88px 1fr 150px 130px 100px 72px'
        : '88px 1fr 150px 130px 72px';

    return (
        <>
            <style>{QAF_CSS}</style>
            <form onSubmit={save} style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 8, alignItems: 'end', marginBottom: 16, flexWrap: 'wrap' }}>
                <div>
                    <div className="sp-qaf-lbl" style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 3 }}>Amount</div>
                    <input style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} type="number" min="0.01" step="0.01" placeholder="£0.00"
                        value={form.amount} onChange={f('amount')} required />
                </div>
                <div>
                    <div className="sp-qaf-lbl" style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 3 }}>Description</div>
                    <input style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} placeholder="e.g. Tesco shop" value={form.description} onChange={f('description')} required />
                </div>
                <div className="sp-qaf-cat">
                    <div className="sp-qaf-lbl" style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 3 }}>Category</div>
                    {useCustom ? (
                        <input style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} placeholder="Category name"
                            value={form.customCat} onChange={f('customCat')} autoFocus />
                    ) : (
                        <select style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} value={form.category}
                            onChange={e => { if (e.target.value === '__custom__') { setUseCustom(true); } else { setForm(p => ({ ...p, category: e.target.value })); } }}>
                            {categories.map(c => <option key={c} value={c}>{catEmoji(c)} {c}</option>)}
                            <option value="__custom__">+ Custom...</option>
                        </select>
                    )}
                </div>
                <div className="sp-qaf-date">
                    <div className="sp-qaf-lbl" style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 3 }}>Date</div>
                    <input style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} type="date" value={form.spentAt} onChange={f('spentAt')} />
                </div>
                {members.length > 0 && (
                    <div className="sp-qaf-who">
                        <div className="sp-qaf-lbl" style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 3 }}>Who</div>
                        <select style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} value={form.householdMemberId} onChange={f('householdMemberId')}>
                            <option value="">Me</option>
                            {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                    </div>
                )}
                <div className="sp-qaf-add" style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button type="submit" style={{ ...btnPrimary, width: '100%' }} disabled={saving}>{saving ? '...' : 'Add'}</button>
                </div>
            </form>
        </>
    );
}

// ── Transaction list grouped by day ──────────────────────────────────────────

function TransactionList({ transactions, onDelete }) {
    const [confirmId, setConfirmId] = useState(null);

    if (transactions.length === 0) {
        return <p style={{ fontSize: 13, color: '#9ca3af', fontStyle: 'italic' }}>No transactions yet -- add one above.</p>;
    }

    const grouped = [];
    let current = null;
    for (const t of transactions) {
        const day = typeof t.spentAt === 'string' ? t.spentAt.slice(0, 10) : new Date(t.spentAt).toISOString().slice(0, 10);
        if (current?.day !== day) {
            current = { day, items: [] };
            grouped.push(current);
        }
        current.items.push(t);
    }

    return (
        <div>
            {grouped.map(g => (
                <div key={g.day}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '10px 0 4px' }}>
                        {fmtDate(g.day)}
                    </div>
                    {g.items.map(t => (
                        <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 10, background: '#fafbfc', marginBottom: 4, border: '1px solid #f3f4f6' }}>
                            <span style={{ fontSize: 18, flexShrink: 0 }}>{catEmoji(t.category)}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1a2e3b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {t.description}
                                    {t.householdMember && (
                                        <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', background: '#f3f4f6', borderRadius: 99, padding: '1px 7px', marginLeft: 6 }}>
                                            {t.householdMember.name}
                                        </span>
                                    )}
                                </div>
                                <div style={{ fontSize: 11, color: '#9ca3af' }}>{t.category}</div>
                            </div>
                            <span style={{ fontSize: 14, fontWeight: 700, color: '#f59e0b', flexShrink: 0 }}>{fmtGBP(t.amount)}</span>
                            {confirmId === t.id ? (
                                <span style={{ display: 'inline-flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
                                    <button style={{ ...btnDanger, padding: '3px 8px' }} onClick={() => { onDelete(t.id); setConfirmId(null); }}>Yes</button>
                                    <button style={{ padding: '3px 8px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 12, cursor: 'pointer' }} onClick={() => setConfirmId(null)}>No</button>
                                </span>
                            ) : (
                                <button style={btnDanger} onClick={() => setConfirmId(t.id)}>×</button>
                            )}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function SpendingSection({ workspaceType, members }) {
    const isPersonal   = workspaceType === 'personal';
    const customCats   = JSON.parse(localStorage.getItem(LS_CUSTOM_CATS) || '[]');
    const baseCategories = isPersonal
        ? [...PERSONAL_CATEGORIES, ...customCats.filter(c => !PERSONAL_CATEGORIES.includes(c))]
        : [...PERSONAL_CATEGORIES, ...BUSINESS_EXTRA, ...customCats.filter(c => !PERSONAL_CATEGORIES.includes(c) && !BUSINESS_EXTRA.includes(c))];

    const [transactions, setTransactions] = useState([]);
    const [report, setReport]             = useState(null);
    const [loading, setLoading]           = useState(true);
    const [error, setError]               = useState('');

    const load = useCallback(async () => {
        setLoading(true); setError('');
        try {
            const month = currentMonthStr();
            const [txRes, rpRes] = await Promise.all([
                api(`/api/spending?month=${month}&limit=60`),
                api(`/api/spending/report?month=${month}`),
            ]);
            if (txRes.ok) setTransactions((await txRes.json()).transactions || []);
            if (rpRes.ok) setReport(await rpRes.json());
        } catch { setError('Could not load spending data.'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);

    async function handleAdd(body) {
        const res = await api('/api/spending', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error('add failed');
        await load();
    }

    async function handleDelete(id) {
        await api(`/api/spending/${id}`, { method: 'DELETE' });
        await load();
    }

    const [open, setOpen] = useState(() => localStorage.getItem('bgt_open_spending') !== '0');
    function toggleSpending() {
        const next = !open;
        setOpen(next);
        localStorage.setItem('bgt_open_spending', next ? '1' : '0');
    }

    return (
        <div style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: open ? 4 : 0, cursor: 'pointer', userSelect: 'none' }} onClick={toggleSpending}>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: '#1a2e3b', margin: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
                    Spending
                    <span style={{ display: 'inline-block', fontSize: 11, color: '#64748b', transition: 'transform 0.2s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', marginLeft: 4, userSelect: 'none' }}>
                        ▶
                    </span>
                </h3>
                {report?.total > 0 && (
                    <span style={{ fontSize: 12, color: '#6b7280' }}>
                        {'£' + Number(report.total).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} this month
                    </span>
                )}
            </div>

            {open && (
                <>
                    {!isPersonal && (
                        <p style={{ fontSize: 12, color: '#9ca3af', margin: '4px 0 10px' }}>
                            Log petty-cash and card spend here. For claimable expenses use{' '}
                            <strong>Expenses</strong>.
                        </p>
                    )}
                    {error && <p style={{ color: '#b91c1c', fontSize: 13 }}>{error}</p>}
                    {loading && <p style={{ color: '#6b7280', fontSize: 13 }}>Loading...</p>}
                    {!loading && (
                        <>
                            <QuickAddForm categories={baseCategories} members={members} onAdd={handleAdd} />
                            <WeekLine weekSummary={report?.weekSummary} />
                            <InsightsPanel insights={report?.insights} />
                            <MonthlyReport report={report} />
                            <TransactionList transactions={transactions} onDelete={handleDelete} />
                        </>
                    )}
                </>
            )}
        </div>
    );
}
